// =============================================================================
// components/ComingSoonBadge.tsx — the "v2" pill for gated crypto surfaces.
//
// A small brand-gradient pill (sparkle + label) that marks any row, card, or
// quick action as a CRYB v2 feature. Drop it next to a label wherever a crypto
// surface is visible-but-gated (v1 policy: crypto is SHOWN for FOMO, blocked by
// lib/comingSoon). Built strictly on theme tokens — no new hues.
//
//   <ComingSoonBadge />                 → gradient "v2" pill
//   <ComingSoonBadge label="Soon" />    → custom label
//   <ComingSoonBadge subtle />          → glass (non-gradient) variant for rows
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from './ui/Icon';
import { colors, fonts, radius, spacing } from '../constants/theme';

export interface ComingSoonBadgeProps {
  /** Badge text. Default "v2". */
  label?: string;
  /** Glass (muted) variant for dense rows instead of the gradient pill. */
  subtle?: boolean;
  /** Hide the sparkle glyph (very tight rows). */
  icon?: boolean;
}

export default function ComingSoonBadge({ label = 'v2', subtle = false, icon = true }: ComingSoonBadgeProps) {
  const content = (
    <View style={styles.row}>
      {icon ? (
        <Ionicons name="sparkles" size={10} color={subtle ? colors.primary : colors.white} />
      ) : null}
      <Text style={[styles.text, subtle && styles.textSubtle]}>{label}</Text>
    </View>
  );

  if (subtle) {
    return <View style={[styles.pill, styles.pillSubtle]}>{content}</View>;
  }

  return (
    <LinearGradient
      colors={colors.gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.pill}
    >
      {content}
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  pill: {
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    alignSelf: 'flex-start',
  },
  pillSubtle: {
    backgroundColor: 'rgba(108,92,231,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(108,92,231,0.18)',
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  text: { ...fonts.bold, fontSize: 10, letterSpacing: 0.6, color: colors.white, textTransform: 'uppercase' },
  textSubtle: { color: colors.primary },
});
